import { Type } from 'class-transformer'
import { BaseEntity } from './base'
import { Case } from './case'
import { PagingQuery } from './common'
import { Court } from './court'
import { BaseDeadline } from './deadline'
import { BaseMassiveEmailReceiver, MassiveEmail } from './massive-email'
import { PracticePhase, PracticeType } from './practice-type'
import type { RecordTool } from './record-tool'
import { User } from './user'

export enum CourtRegistry {
  CC = 'CC',
}

export class PracticeRow extends BaseEntity {
  displayName!: string
  createdAt!: Date
  archiveNumber!: string
  externalId!: string | null
  registry!: CourtRegistry | null
  generalRoleNumber!: string | null
  eurValueOverride!: string | null
  courtId!: string | null
  typeId!: string
  phaseId!: string
  caseId!: string
  parentId!: string | null
  mergedIntoId!: string | null

  case!: {
    displayName: string
  }

  @Type(() => Court)
  court?: Court | null

  type!: {
    displayName: string
    icon: string
  }

  phase!: {
    displayName: string
    color: string
  }

  get judicialData() {
    if (this.court == null) {
      return null
    }
    return [this.court.displayName, this.registry, this.generalRoleNumber].filter((x) => x).join(' - ')
  }
}

export class PracticeAssignment extends BaseEntity {
  @Type(() => User)
  user!: User
  createdAt!: Date
  role!: string
  defaultDeadlineAssignment!: boolean
}

export class BasePractice extends BaseEntity {
  displayName!: string
  createdAt!: Date
  archiveNumber!: string
  externalId!: string | null
  registry!: CourtRegistry | null
  generalRoleNumber!: string | null
  eurValueOverride!: string | null
  courtId!: string | null
  typeId!: string
  phaseId!: string
  caseId!: string
  parentId!: string | null
  mergedIntoId!: string | null
}

export function formattedJudicialData(
  court?: Court | null,
  registry?: CourtRegistry | null,
  generalRoleNumber?: string | null,
) {
  if (court == null) {
    return '-'
  }
  const rows = [court.authority + ' ' + court.site, registry]
  if (generalRoleNumber) {
    rows.push('R.G. ' + generalRoleNumber)
  }
  return rows.join('\n')
}

export class Practice extends BasePractice {
  @Type(() => PracticeAssignment)
  userAssignments!: PracticeAssignment[]
  @Type(() => Court)
  court?: Court | null
  @Type(() => PracticeType)
  type!: PracticeType
  @Type(() => PracticePhase)
  phase!: PracticePhase
  @Type(() => Case)
  case!: Case

  public get actualEurValue() {
    return this.eurValueOverride ?? this.case?.eurValue ?? null
  }
}

export interface GetPractices {
  practices: PracticeRow[]
  hasNextPage: boolean
}

export interface GetPractice {
  practice: Practice
}

export interface GetPracticesQuery extends PagingQuery {
  searchString?: string
  phaseIds?: string[]
  externalId?: string
  courtId?: string
  generalRoleNumber?: string
  userId?: string | null

  /**
   * @deprecated use phaseIds instead
   */
  typeId?: string
  /**
   * @deprecated use phaseIds instead
   */
  phaseId?: string
  /**
   * @deprecated use phaseIds instead
   */
  archived?: string
}

export class NewPractice {
  displayName!: string
  archiveNumber!: string
  registry!: CourtRegistry | null
  generalRoleNumber!: string | null
  eurValueOverride!: number | null
  courtId!: string | null
  typeId!: string
  phaseId!: string
  parentId!: string | null
  externalId!: string | null
  userAssignments?: NewPracticeAssignment[]
  cloneSubjectIds?: string[]
}

export interface PracticeCreated {
  practice: {
    id: string
  }
}

export class EditPractice {
  displayName?: string
  archiveNumber?: string
  externalId?: string | null
  parentId?: string | null
  mergedIntoId?: string | null
  courtId?: string | null
  registry?: CourtRegistry | null
  generalRoleNumber?: string | null
  typeId?: string
  phaseId?: string
  eurValueOverride?: number | null
}

export class TransferPractice {
  caseId?: string
  createNewCase?: true
}

export interface PracticeEdited {
  practice: BasePractice
}

export class NewPracticeAssignment {
  userId!: string
  role!: string
  defaultDeadlineAssignment?: boolean
  assignToUnfulfilledDeadlines?: boolean
}

export class EditPracticeAssignment {
  defaultDeadlineAssignment?: boolean
}

export interface PracticeAssignmentWritten {
  practiceAssignment: PracticeAssignment
}

export interface GetRunnableRecordTools {
  recordTools: RecordTool[]
}

export class NewRecordToolRun {
  toolKey!: string
}

class PracticeMassiveEmailResponse extends BaseEntity {
  createdAt!: string
  response!: string
}

class PracticeMassiveEmailReceiver extends BaseMassiveEmailReceiver {
  @Type(() => PracticeMassiveEmailResponse)
  responses!: PracticeMassiveEmailResponse[]
}

export class PracticeMassiveEmail extends MassiveEmail {
  @Type(() => PracticeMassiveEmailReceiver)
  receivers!: PracticeMassiveEmailReceiver[]
}

export interface GetPracticeMassiveEmails {
  massiveEmails: PracticeMassiveEmail[]
}

class PracticeDeadlineUserAssignment extends BaseEntity {
  userId!: string
}

export class PracticeDeadline extends BaseDeadline {
  userDeadlines!: PracticeDeadlineUserAssignment[]
}

export interface GetPracticeDeadlines {
  deadlines: PracticeDeadline[]
}
